import PageLayout from "../../layouts/PageLayout";
import {Card, SubCard} from "../../components/card/Card.tsx";
import {getAddress, isAddress} from "viem";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {type SubmitHandler, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {openExplorerInNewTab} from "../../utils/explorerHelper.ts";
import {deleteNickname, getNicknames, type Nickname, setNickname,} from "../../repository/NicknamesRepository.ts";
import {useAccount, useSignMessage} from "wagmi";
import {StaffGuard} from "../../guards/StaffGuard.tsx";
import {ExternalLinkIcon, TrashIcon} from "lucide-react";
import {StaffType} from "../../hooks/useStaffGuard.ts";

const Nicknames = () => {
  const { data: nicknames } = useQuery({
    queryKey: ["nicknames"],
    queryFn: () => getNicknames(),
  });

  return (
    <PageLayout
      breadcrumbs={[
        { name: "Factory", href: "/factory" },
        { name: "Nicknames" },
      ]}
    >
        <StaffGuard staffTypes={[StaffType.CONTROLLER, StaffType.OWNER]}>
      <Card title={"Nicknames"}>
        <SubCard>
          <ListNicknames nicknames={nicknames || []} />
          <div className="divider" />
          <AddNicknameForm />
        </SubCard>
      </Card>
        </StaffGuard>
    </PageLayout>
  );
};

const AddNicknameForm = () => {
  const queryClient = useQueryClient();
  const { address } = useAccount();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ address: string; nickname: string }>();

  const { signMessageAsync } = useSignMessage();

  const onSubmit: SubmitHandler<{ address: string; nickname: string }> = async (
    data,
  ) => {
    if (!address) {
      return;
    }
    const signature = await signMessageAsync({
      message: `Set nickname ${data.nickname} for address ${data.address}`,
    });

    try {
      await setNickname(
        address,
        data.address,
        data.nickname,
        signature.toString(),
      );
      toast.success("Nickname set successfully!");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      toast.error("Error setting nickname");
    }

    reset();
    await queryClient.invalidateQueries({ queryKey: ["nicknames"] });
  };

  return (
    <form
      className="flex flex-row gap-2 items-start"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        <input
          {...register("address", {
            required: "Address is required",
            validate: (value) => isAddress(value) || "Invalid Ethereum address",
          })}
          type="text"
          placeholder="Address"
          className="input input-bordered w-170 mb-1"
          maxLength={42}
        />
        {errors.address && (
          <p className="text-error text-sm">{errors.address.message}</p>
        )}
      </div>

      <div className="flex-1">
        <input
          {...register("nickname", { required: "Nickname is required" })}
          type="text"
          placeholder="Nickname"
          className="input input-bordered w-full mb-1"
        />
        {errors.nickname && (
          <p className="text-error text-sm">{errors.nickname.message}</p>
        )}
      </div>
      <button type="submit" className="btn btn-primary">
        Set Nickname
      </button>
    </form>
  );
};

const ListNicknames = ({ nicknames }: { nicknames: Nickname[] }) => {
  const queryClient = useQueryClient();
  const { address: senderAddress } = useAccount();

  const { signMessageAsync } = useSignMessage();

  const handleRemove = async (address: string) => {
    if (!address) return;
    if (!senderAddress) {
      return;
    }

    const signature = await signMessageAsync({
      message: `Delete nickname for address ${address}`,
    });

    try {
      await deleteNickname(senderAddress, address, signature.toString());
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      toast.error("Error removing nickname");
      return;
    }
    toast.success("Nickname removed successfully!");

    await queryClient.invalidateQueries({ queryKey: ["nicknames"] });
  };

  return (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Address</th>
            <th>Nickname</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {nicknames.map((nickname) => (
            <tr>
              <td>
                <div>{getAddress(nickname.address)}</div>
              </td>
              <td className="w-full">{nickname.nickname}</td>
              <td className="flex flex-row gap-2">
                <button
                  className="btn btn-sm"
                  onClick={() => openExplorerInNewTab(nickname.address)}
                >
                  <ExternalLinkIcon />
                </button>
                {nickname.nickname == null ? (
                  <div className="tooltip" data-tip="hello">
                    <button
                      className="btn btn-error btn-sm text-white"
                      onClick={() => handleRemove(nickname.address)}
                      disabled={true}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-error btn-sm text-white"
                    onClick={() => handleRemove(nickname.address)}
                  >
                    <TrashIcon />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Nicknames;
